// @flow
import React from 'react';
import { get } from 'lodash';
import classNames from 'classnames';
import { type FieldProps } from 'formik';

import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { ICONS } from 'Components/utils/icon/icons';
import { getValidationStatus } from 'Components/frameworks/formik/inputsUtils';

type Props = {
  label?: string | Node,
  isRequired: boolean,
  isDisabled?: boolean,
  isLoading?: boolean,
  isReadOnly?: boolean,
  ...FieldProps,
};

const SIZE = 25;

export const Radio = ({
  field,
  form,
  isDisabled,
  isLoading,
  isReadOnly,
  isRequired,
  label,
  onChange,
}: Props) => {
  const formValue = get(form.values, field.name, '');
  const { isErrored } = getValidationStatus({ ...field, formValue }, form);
  // values can be integer at form initialize but finally it's always a string, so we ensure it
  const isChecked = formValue.toString() === field.value.toString();

  return (
    <div
      className={classNames('flex justify-between text-base', {
        'text-red formik-errored': isErrored && isRequired,
        'italic opacity-50': isDisabled,
      })}
    >
      <label
        htmlFor={`radio-${field.name}-${field.value}`}
        className={classNames('flex items-center w-full', {
          'cursor-pointer': !isDisabled,
          'cursor-not-allowed': isDisabled,
        })}
      >
        <span
          className={classNames(
            'border border-gray-medium flex-shrink-0 inline-block leading-none rounded-half text-blue rtl:mr-3 mt-px',
            {
              'cursor-pointer': !isDisabled && !isLoading && !isReadOnly,
              'cursor-not-allowed bg-gray-lighter': isDisabled,
              'cursor-wait': isLoading,
              'text-blue': isChecked,
            },
          )}
          style={{ width: SIZE, height: SIZE, padding: '5px' }}
        >
          <Icon
            className={classNames({
              'opacity-0': !isChecked,
              'opacity-50': isChecked && isDisabled,
            })}
            name={ICONS.FAT_DOT}
            width="100%"
          />
        </span>
        <input
          {...field}
          checked={isChecked}
          className="a11y"
          disabled={isDisabled}
          id={`radio-${field.name}-${field.value}`}
          type="radio"
          onChange={event => {
            field.onChange(event);

            if (onChange) {
              onChange(event);
            }
          }}
        />
        {label && <div className="leading-tight">{label}</div>}
      </label>
    </div>
  );
};
